import React, { useState, useContext, useRef, useEffect } from 'react'
import { Modal, Button, Form } from 'react-bootstrap'
import { ModelContext, useUserService } from '@abroad/components'
import API from '../../utils/API'
import { gtmScheduleCallAction } from '../quests/gtmfunctions'

const getFieldKey = (question) =>
  question.question.replace(/\s+/g, '').toLowerCase()

const isOptionalQuestion = (question) => {
  const questionText = question.question.trim().toLowerCase()
  return (
    /\(\s*optional\s*\)/i.test(question.question) ||
    questionText === 'referral name'
  )
}

const isUrlQuestion = (question) => question.answerType === 'url'

const isReferralNameQuestion = (question) =>
  question.question.trim().toLowerCase() === 'referral name'

const getQuestionLabel = (question) => {
  if (isReferralNameQuestion(question)) {
    return `${question.question} (if applicable)`
  }
  return question.question
}

const isValidHttpUrl = (value) => {
  const trimmed = value.trim()
  if (!trimmed) return false
  try {
    const url = new URL(trimmed)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return /^https?:\/\/.+/i.test(trimmed)
  }
}

const getInitialFormData = (questions) => {
  return questions.reduce((acc, question) => {
    const key = getFieldKey(question)
    return { ...acc, [key]: '' }
  }, {})
}

const ScheduleCallModal = ({
  questId,
  inquiryQuestion = [],
  questInquiryCustomFormId,
  investments,
}) => {
  const { show, onHide: closeModal } = useContext(ModelContext)
  const { user } = useUserService()
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState(() =>
    getInitialFormData(inquiryQuestion),
  )
  const [errors, setErrors] = useState({})
  const [showValidation, setShowValidation] = useState(false)
  const [occupancyPreference, setOccupancyPreference] = useState('')
  const textareaRefs = useRef({})

  // Reset validation state when modal opens
  useEffect(() => {
    if (show) {
      setShowValidation(false)
      setErrors({})
      setIsSubmitted(false) // Reset submission state when modal opens
      setOccupancyPreference('') // Reset occupancy preference
    }
  }, [show])

  const handleModalClose = () => {
    setFormData(getInitialFormData(inquiryQuestion))
    setErrors({}) // Clear all errors when modal closes
    setShowValidation(false)
    setOccupancyPreference('')
    closeModal()
  }

  const validateForm = () => {
    const newErrors = {}
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (!occupancyPreference) {
      newErrors.occupancyPreference = 'Please select an occupancy preference'
    }

    inquiryQuestion.forEach((question) => {
      const key = getFieldKey(question)
      const questionText = question.question.trim().toLowerCase()
      const value = formData[key] || ''

      if (!value.trim() && !isOptionalQuestion(question)) {
        newErrors[key] = 'This field is required'
        return
      }

      if (question.answerType === 'textarea') {
        return
      }

      if (questionText === 'full name' && value.trim().length < 1) {
        newErrors[key] = 'This field is required'
      }

      if (question.answerType === 'email') {
        if (!emailRegex.test(value.trim())) {
          newErrors[key] = 'Must be a valid email address'
        }
      }

      if (isUrlQuestion(question) && value.trim()) {
        if (!isValidHttpUrl(value)) {
          newErrors[key] = 'Please enter a valid URL'
        }
      } else if (isOptionalQuestion(question) && !value.trim()) {
        delete newErrors[key]
      }
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target

    // Get question text to determine field type
    const question = inquiryQuestion.find((q) => getFieldKey(q) === name)

    let processedValue = value
    if (
      question?.answerType === 'email' ||
      (question && isUrlQuestion(question))
    ) {
      processedValue = value.toLowerCase()
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: processedValue,
    }))

    // Clear error when user starts typing (only if validation has been shown)
    if (showValidation && errors[name]) {
      if (question && isUrlQuestion(question)) {
        if (!processedValue.trim() || isValidHttpUrl(processedValue)) {
          setErrors((prev) => ({ ...prev, [name]: '' }))
        }
      } else if (processedValue.trim()) {
        setErrors((prev) => ({ ...prev, [name]: '' }))
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Always show validation on submit attempt
    setShowValidation(true)

    // Validate form and stop if validation fails
    const isValid = validateForm()

    if (!isValid) {
      // Validation failed, errors are now visible - DO NOT proceed with API call

      return
    }

    // Only proceed with API call if ALL validations pass
    setIsLoading(true)
    try {
      // Transform form data into the required API format with questionId mapping
      const answers = inquiryQuestion.map((question) => {
        const fieldName = getFieldKey(question)
        const questionAnswer = formData[fieldName] || ''

        if (
          (isOptionalQuestion(question) || isUrlQuestion(question)) &&
          !questionAnswer.trim()
        ) {
          return {
            questionId: question.id,
            answer: [],
          }
        }

        return {
          questionId: question.id,
          answer: [questionAnswer],
        }
      })

      const payload = {
        answers: answers,
        occupancyPreference: occupancyPreference.toLowerCase(),
      }

      await API.quests.postQuestInquiry(
        questId,
        questInquiryCustomFormId,
        payload,
      )
      setIsSubmitted(true)

      // GTM event tracking
      if (user?.id) {
        gtmScheduleCallAction(questId, 'Experience', user.id)
      }

      setFormData(getInitialFormData(inquiryQuestion))
      setErrors({}) // Clear errors after successful submission
      setShowValidation(false)
      setOccupancyPreference('')
    } catch (error) {
      console.error('API call failed:', error)
      setErrors({ submit: 'Failed to submit form. Please try again.' })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      show={show}
      onHide={handleModalClose}
      centered
      backdrop='static'
      dialogClassName={`schedule-call-modal-dialog upgrade-inquiry schedule-call-modal ${
        isSubmitted ? 'thank-you-modal' : ''
      }`}
      keyboard={false}>
      <Modal.Header className='payment-modal-header text-black px-30px s3 justify-content-between'>
        Experience Application
        <svg
          className='cursor-pointer'
          onClick={handleModalClose}
          xmlns='http://www.w3.org/2000/svg'
          width='24'
          height='24'
          viewBox='0 0 24 24'
          fill='none'>
          <rect
            x='24'
            y='24'
            width='24'
            height='24'
            rx='12'
            transform='rotate(-180 24 24)'
            fill='#EBEBEB'
          />
          <path d='M17 7L7 17' stroke='black' stroke-linecap='round' />
          <path d='M17 17L7 7' stroke='black' stroke-linecap='round' />
        </svg>
      </Modal.Header>
      <Modal.Body>
        {isSubmitted ? (
          <>
            <div className='text-center mt-40px s2'>Thank you!</div>
            <p className='text-center mt-20px mb-40px s6'>
              Your form has been submitted. A member of our
              <br /> team will be in touch shortly with the next steps.
            </p>
          </>
        ) : (
          <Form onSubmit={handleSubmit} noValidate>
            {inquiryQuestion.map((question, index) => {
              const fieldName = getFieldKey(question)
              const isTextarea = question.answerType === 'textarea'
              const isUrlField = isUrlQuestion(question)
              const fieldValue = formData[fieldName] || ''

              return (
                <React.Fragment key={question.id || index}>
                  <Form.Group className='mb-3'>
                    {question.description && (
                      <div className='s3 mb-2'>{question.description}</div>
                    )}
                    <Form.Label
                      className={
                        isTextarea
                          ? 'quest-text text-black d-block mb-2 mt-0'
                          : 'quest-modal-lable'
                      }>
                      {getQuestionLabel(question)}{' '}
                    </Form.Label>

                    <div className='linkedin-profile-input'>
                      <Form.Control
                        as={isTextarea ? 'textarea' : 'input'}
                        type={isTextarea ? null : question.answerType || 'text'}
                        name={fieldName}
                        value={fieldValue}
                        onChange={handleChange}
                        placeholder={question.placeholder || ''}
                        isInvalid={false}
                        readOnly={false}
                        required={false}
                        autoComplete='off'
                        data-validate='false'
                        rows={1}
                        className={`${
                          isTextarea ? 'auto-resize-textarea' : ''
                        }`}
                        style={{
                          minHeight: isTextarea ? '80px' : 'auto',
                          resize: 'none',
                          overflow: 'hidden',
                        }}
                        ref={(el) => {
                          if (isTextarea) {
                            textareaRefs.current[fieldName] = el
                          }
                        }}
                        onInput={(e) => {
                          if (isTextarea) {
                            e.target.style.height = 'auto'
                            e.target.style.height = `${e.target.scrollHeight}px`
                          }
                        }}
                      />
                      {showValidation && errors[fieldName] && (
                        <div
                          style={{
                            fontSize: '0.875rem',
                            color: '#dc3545',
                            fontWeight: '500',
                            marginTop: '8px',
                            display: 'block',
                            visibility: 'visible',
                            lineHeight: '1.2',
                          }}>
                          {errors[fieldName]}
                        </div>
                      )}
                    </div>
                  </Form.Group>
                  {isUrlField && (
                    <Form.Group className='mb-3'>
                      <Form.Label className='quest-modal-lable'>
                        Occupancy Preference
                      </Form.Label>
                      <Form.Select
                        value={occupancyPreference}
                        style={{ border: '1px solid #000' }}
                        onChange={(e) => {
                          setOccupancyPreference(e.target.value)
                          if (showValidation && errors.occupancyPreference) {
                            setErrors((prev) => ({
                              ...prev,
                              occupancyPreference: '',
                            }))
                          }
                        }}>
                        <option value=''>Select occupancy preference</option>
                        <option value='single'>
                          Single ($
                          {investments?.singleSupplement
                            ? investments.singleSupplement.toLocaleString(
                                'en-US',
                              )
                            : '0'}
                          )
                        </option>
                        <option value='double'>
                          Double ($
                          {investments?.standardRate
                            ? investments.standardRate.toLocaleString('en-US')
                            : '0'}
                          )
                        </option>
                      </Form.Select>
                      {showValidation && errors.occupancyPreference && (
                        <div
                          style={{
                            fontSize: '0.875rem',
                            color: '#dc3545',
                            fontWeight: '500',
                            marginTop: '8px',
                            display: 'block',
                            visibility: 'visible',
                            lineHeight: '1.2',
                          }}>
                          {errors.occupancyPreference}
                        </div>
                      )}
                    </Form.Group>
                  )}
                </React.Fragment>
              )
            })}

            {errors.submit && (
              <div className='alert alert-danger' role='alert'>
                {errors.submit}
              </div>
            )}
            <div className='d-flex justify-content-end pt-3'>
              <Button
                variant='secondary-light'
                onClick={handleModalClose}
                className='mr-3 secondary-popup-button font-bold button-padding me-3'
                size='sm'
                disabled={isLoading}>
                CANCEL
              </Button>
              <button
                type='submit'
                className='btn btn-gold text-white border-radius-5 text-uppercase button-padding prosperity-button-cta small'>
                {isLoading ? 'SUBMITTING...' : 'SUBMIT'}
              </button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default ScheduleCallModal
